<template>
  <div
    class="agent-message-wrap group"
    :class="{
      'has-response': hasRecordedResponse || isASubmittedForm,
    }"
  >
    <div v-if="!isASubmittedForm" class="agent-message">
      <div class="avatar-wrap">
        <thumbnail
          v-if="message.showAvatar || hasRecordedResponse"
          :src="avatarUrl"
          size="24px"
          :username="agentName"
        />
      </div>
      <div class="message-wrap">
        <div v-if="hasReplyTo" class="flex mt-2 mb-1 text-xs">
          <reply-to-chip :reply-to="replyTo" />
        </div>
        <div class="flex gap-1">
          <drag-wrapper
            class="space-y-2"
            direction="right"
            @dragged="toggleReply"
          >
            <AgentMessageBubble
              v-if="shouldDisplayAgentMessage"
              :content-type="contentType"
              :message-content-attributes="messageContentAttributes"
              :message-id="message.id"
              :message-type="messageType"
              :message="{
                ...message,
                content:
                  messageSender.type === 'ai_agent'
                    ? messageWithoutSignature
                    : message.content,
              }"
            />
            <div
              v-if="hasAttachments"
              class="chat-bubble has-attachment agent"
              :class="(wrapClass, $dm('bg-white', 'dark:bg-slate-700'))"
            >
              <div
                v-for="attachment in message.attachments"
                :key="attachment.id"
              >
                <image-bubble
                  v-if="attachment.file_type === 'image' && !hasImageError"
                  :url="attachment.data_url"
                  :thumb="attachment.data_url"
                  :readable-time="readableTime"
                  @error="onImageLoadError"
                />
                <audio v-else-if="attachment.file_type === 'audio'" controls>
                  <source :src="attachment.data_url" />
                </audio>
                <file-bubble v-else :url="attachment.data_url" />
              </div>
            </div>
          </drag-wrapper>
          <div class="flex flex-col justify-end">
            <ChatMessageReplyButtonV2
              v-if="isEnabledWidgetV2"
              class="transition-all delay-75 opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 sm:opacity-0"
              @click="toggleReply"
            />
            <message-reply-button
              v-else
              class="transition-opacity delay-75 opacity-0 group-hover:opacity-100 sm:opacity-0"
              @click="toggleReply"
            />
          </div>
        </div>
        <p
          v-if="message.showAvatar || hasRecordedResponse"
          v-dompurify-html="agentName"
          class="agent-name"
          :class="$dm('text-secondary', 'dark:text-secondary-dark')"
        />
      </div>
    </div>

    <UserMessage v-if="hasRecordedResponse" :message="responseMessage" />
    <div v-if="isASubmittedForm">
      <UserMessage
        v-for="submittedValue in submittedFormValues"
        :key="submittedValue.id"
        :message="submittedValue"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import UserMessage from 'widget/components/UserMessage.vue';
import AgentMessageBubble from 'widget/components/AgentMessageBubble.vue';
import MessageReplyButton from 'widget/components/MessageReplyButton.vue';
import ChatMessageReplyButtonV2 from 'widget/v2/components/message-parts/ChatMessageReplyButton.vue';
import timeMixin from 'dashboard/mixins/time';
import { useFlipper } from 'widget/composables/useFlipper';
import ImageBubble from 'widget/components/ImageBubble.vue';
import FileBubble from 'widget/components/FileBubble.vue';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import configMixin from '../mixins/configMixin';
import messageMixin from '../mixins/messageMixin';
import { isASubmittedFormMessage } from 'shared/helpers/MessageTypeHelper';
import darkModeMixin from 'widget/mixins/darkModeMixin.js';
import ReplyToChip from 'widget/components/ReplyToChip.vue';
import DragWrapper from 'widget/components/DragWrapper.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';

export default {
  name: 'AgentMessage',
  components: {
    AgentMessageBubble,
    ImageBubble,
    Thumbnail,
    UserMessage,
    FileBubble,
    MessageReplyButton,
    ChatMessageReplyButtonV2,
    ReplyToChip,
    DragWrapper,
  },
  mixins: [timeMixin, configMixin, messageMixin, darkModeMixin],
  props: {
    message: {
      type: Object,
      default: () => {},
    },
    replyTo: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    const { isEnabled } = useFlipper();
    return { isFlipperEnabled: isEnabled };
  },
  data() {
    return {
      hasImageError: false,
    };
  },
  computed: {
    ...mapGetters({
      aiAgent: 'aiAgent/aiAgent',
    }),
    isEnabledWidgetV2() {
      return this.isFlipperEnabled('chat_widget_v2').value;
    },
    messageSender() {
      return this.message.sender || {};
    },
    shouldDisplayAgentMessage() {
      if (
        this.contentType === 'input_select' &&
        this.messageContentAttributes.submitted_values &&
        !this.message.content
      ) {
        return false;
      }
      return this.message.content;
    },
    messageWithoutSignature() {
      // Only process for AI agent messages with content and signature
      if (
        this.message.sender?.type !== 'ai_agent' ||
        !this.message.content ||
        !this.message.sender?.signature
      ) {
        return this.message.content;
      }

      const agentSignature = this.message.sender?.signature || '';

      try {
        // Split by signature and check the end
        const parts = this.message.content.split(agentSignature);
        if (parts.length > 1) {
          const lastPart = parts[parts.length - 1];
          // Check if the last part is only whitespace
          const trimmed = lastPart.trim();

          if (trimmed.length === 0) {
            // If it's all whitespace, remove it
            parts.pop();
            return parts.join(agentSignature).trim();
          }
        }

        // If signature can't be safely removed, return original content
        return this.message.content;
      } catch (error) {
        // If any error occurs, return original content
        return this.message.content;
      }
    },
    readableTime() {
      const { created_at: createdAt = '' } = this.message;
      return this.messageStamp(createdAt, 'LLL d yyyy, h:mm a');
    },
    messageType() {
      const { message_type: type = 1 } = this.message;
      return type;
    },
    contentType() {
      const { content_type: type = '' } = this.message;
      return type;
    },
    agentName() {
      if (this.message.sender) {
        return this.message.sender.available_name || this.message.sender.name;
      }

      if (this.useInboxAvatarForBot) {
        return this.channelConfig.websiteName;
      }

      return this.$t('UNREAD_VIEW.BOT');
    },
    avatarUrl() {
      // eslint-disable-next-line
      const BotImage = require('dashboard/assets/images/chatwoot_bot.png');
      // eslint-disable-next-line
      const AIAgentImage = require('dashboard/assets/images/integrations/ai-agent.svg');

      // Check if message is a bot message, message_type === 3 in widget equivalent to bot
      const isBotMessage = this.message.message_type === MESSAGE_TYPE.TEMPLATE;

      // If sender has an avatar (including AI agents), use it
      if (this.message.sender?.avatar_url) {
        return this.message.sender.avatar_url;
      }

      // Use AI agent icon for AI agents without avatar
      if (this.message.sender?.type === 'ai_agent') {
        return AIAgentImage;
      }

      // Use AI agent icon for bot messages
      if (isBotMessage) {
        return this.aiAgent?.avatarUrl || AIAgentImage;
      }

      // For user type without avatar, return empty string to show name only
      if (this.message.sender?.type === 'user') {
        return '';
      }

      // Fall back to inbox avatar or empty string to show name only
      return this.useInboxAvatarForBot ? this.inboxAvatarUrl : '';
    },
    hasRecordedResponse() {
      return (
        this.messageContentAttributes.submitted_email ||
        (this.messageContentAttributes.submitted_values &&
          !['form', 'input_csat'].includes(this.contentType))
      );
    },
    responseMessage() {
      if (this.messageContentAttributes.submitted_email) {
        return { content: this.messageContentAttributes.submitted_email };
      }

      if (this.messageContentAttributes.submitted_values) {
        if (this.contentType === 'input_select') {
          const [selectionOption = {}] =
            this.messageContentAttributes.submitted_values;
          return { content: selectionOption.title || selectionOption.value };
        }
      }
      return '';
    },
    isASubmittedForm() {
      return isASubmittedFormMessage(this.message);
    },
    submittedFormValues() {
      return this.messageContentAttributes.submitted_values.map(
        submittedValue => ({
          id: submittedValue.name,
          content: submittedValue.value,
        })
      );
    },
    wrapClass() {
      return {
        'has-text': this.shouldDisplayAgentMessage,
      };
    },
    hasReplyTo() {
      return this.replyTo && (this.replyTo.content || this.replyTo.attachments);
    },
  },
  watch: {
    message() {
      this.hasImageError = false;
    },
  },
  mounted() {
    this.hasImageError = false;
  },
  methods: {
    onImageLoadError() {
      this.hasImageError = true;
    },
    toggleReply() {
      bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.message);
    },
  },
};
</script>
